/**
 * Campaign schedule date/time helpers shared by API and staff UI.
 * API stores calendar date and time-of-day separately; callers combine them into one Date.
 *
 * Contract with the staff UI:
 * - schedule_*_date is often an ISO instant from `Date#toISOString()`
 * - schedule_*_time is local wall-clock HH:MM:SS from `formatTimeFromDate`
 * Rebuilding must use local Y/M/D (not UTC). UTC calendar parts shift the day when the
 * local offset crosses midnight relative to that instant.
 */

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Local Y/M/D from `date` + H:M:S from `scheduleTime` (HH:MM:SS).
 * @param {Date} date
 * @param {string} scheduleTime
 * @returns {Date}
 */
function combineDatePartsLocalYmdWithTime(date, scheduleTime) {
  const [hours, minutes, seconds] = String(scheduleTime).split(':').map(Number);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours || 0,
    minutes || 0,
    seconds || 0
  );
}

/**
 * @deprecated Use combineDatePartsLocalYmdWithTime. Kept for existing API imports.
 * @param {Date} date
 * @param {string} scheduleTime
 * @returns {Date}
 */
function combineDatePartsUtcYmdWithTime(date, scheduleTime) {
  return combineDatePartsLocalYmdWithTime(date, scheduleTime);
}

/**
 * Merge a schedule date field with an optional companion time field.
 * @param {string|Date|null|undefined} dateValue
 * @param {string|null|undefined} timeValue
 * @returns {Date|null}
 */
function buildDateTimeFromParts(dateValue, timeValue) {
  if (!dateValue) {
    return null;
  }

  // Pure YYYY-MM-DD: parse calendar parts directly (Date.parse treats it as UTC midnight).
  if (typeof dateValue === 'string' && timeValue) {
    const trimmed = dateValue.trim();
    if (DATE_ONLY_RE.test(trimmed)) {
      const [year, month, day] = trimmed.split('-').map(Number);
      const [hours, minutes, seconds] = String(timeValue).split(':').map(Number);
      return new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0);
    }
  }

  const baseDate = new Date(dateValue);
  if (Number.isNaN(baseDate.getTime())) {
    return null;
  }
  if (!timeValue) {
    return baseDate;
  }
  return combineDatePartsLocalYmdWithTime(baseDate, timeValue);
}

/**
 * Local HH:MM:SS for schedule_start_time / schedule_end_time API fields.
 * @param {Date|null|undefined} date
 * @returns {string|null}
 */
function formatTimeFromDate(date) {
  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

module.exports = {
  combineDatePartsLocalYmdWithTime,
  combineDatePartsUtcYmdWithTime,
  buildDateTimeFromParts,
  formatTimeFromDate,
};
